/**
 * Per-space viewport memory.
 *
 * Remembers pan/zoom/focal for EACH space (keyed by `spaceOfPath().path`), so
 * switching the active package/space — VSCode-style, by switching the open file
 * — returns you to where you last were in that space, instead of carrying one
 * shared viewport across spaces (which drifts off-center on every switch).
 *
 * Backed by localStorage so it survives reloads; an in-memory cache fronts it so
 * same-session reads never touch storage. Every failure (quota, disabled, SSR,
 * malformed JSON) collapses to "no memory, fall back to a fit" — this is a UX
 * nicety, never a correctness requirement.
 *
 * Only `{focalPath, center, zoom}` is stored — `Viewport.focalRects` is omitted
 * on purpose: a restored viewport is a direct jump and `popFocal` re-measures
 * (see the `focalRects` docs in scene/board/viewport). The viewport is
 * centre-canonical, so the remembered view is size-independent: returning to a
 * space at a different window size keeps the same centre, not the same pan.
 */
import type { Viewport } from "@archlang/scene/board";
import { hostStorage } from "../host/hostStorage.js";
import type { PersistedViewport } from "../viewerStorage.js";

// v2: centre-canonical (`center` not `pan`). Old pan entries are ignored.
const KEY_PREFIX = "archlang.spaceViewport.v2";

export interface SpaceViewportMemory {
  /** Remembered viewport for a space, or undefined (→ caller fits). */
  get(spaceKey: string | null): Viewport | undefined;
  /** Remember the viewport for a space. No-op when spaceKey is null. */
  set(spaceKey: string | null, vp: Viewport): void;
}

export function createSpaceViewportMemory(namespace: string): SpaceViewportMemory {
  const cache = new Map<string, PersistedViewport>();
  const storageKey = (k: string): string => `${KEY_PREFIX}:${namespace}:${k}`;
  return {
    get(spaceKey) {
      if (spaceKey === null) return undefined;
      let vp = cache.get(spaceKey);
      if (!vp) {
        const read = readStorage(storageKey(spaceKey));
        if (read) cache.set(spaceKey, (vp = read));
      }
      return vp ? { focalPath: vp.focalPath, center: vp.center, zoom: vp.zoom } : undefined;
    },
    set(spaceKey, vp) {
      if (spaceKey === null) return;
      const slim: PersistedViewport = { focalPath: vp.focalPath, center: vp.center, zoom: vp.zoom };
      cache.set(spaceKey, slim);
      writeStorage(storageKey(spaceKey), slim);
    },
  };
}

function readStorage(key: string): PersistedViewport | undefined {
  try {
    const raw = hostStorage.getItem(key);
    if (!raw) return undefined;
    const p = JSON.parse(raw) as PersistedViewport;
    if (!Array.isArray(p?.focalPath)) return undefined;
    if (typeof p.zoom !== "number" || !Number.isFinite(p.zoom)) return undefined;
    if (typeof p.center?.x !== "number" || typeof p.center?.y !== "number") return undefined;
    return p;
  } catch {
    return undefined;
  }
}

function writeStorage(key: string, vp: PersistedViewport): void {
  // Quota / disabled storage is swallowed by the seam — a UX nicety, not
  // correctness.
  hostStorage.setItem(key, JSON.stringify(vp));
}
